
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UserRole, User } from '../types';
import { authService } from '../services/auth.service';

interface AuthContextType {
  token: string | null;
  user: User | null;
  userRole: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Simple JWT decoder helper
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(localStorage.getItem('auth_role') as UserRole);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    authService.logout();
    setToken(null);
    setUserRole(null);
    setUser(null);
  }, []);

  const fetchProfile = useCallback(async (tokenToUse: string) => {
    try {
      const profileResponse = await authService.getProfile();
      if (profileResponse.status) {
        const userData = profileResponse.data;
        setUser(userData);
        
        // Decoding JWT for role confirmation
        const decoded = decodeJWT(tokenToUse);
        const role = (decoded?.admin === 1 || userData.is_admin === 1) ? 'admin' : 'user';
        
        setUserRole(role);
        localStorage.setItem('auth_role', role);
      }
    } catch (error) {
      console.error("Failed to fetch profile", error);
      // Only logout if it's explicitly an auth error, but for robustness:
      if (error instanceof Error && error.message.includes('server')) {
        // Just network error, don't logout
      } else {
        logout();
      }
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  const login = async (newToken: string) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    const decoded = decodeJWT(newToken);
    const role = decoded?.admin === 1 ? 'admin' : 'user';
    setUserRole(role);
    localStorage.setItem('auth_role', role);
    await fetchProfile(newToken);
  };

  useEffect(() => {
    if (token) {
      fetchProfile(token);
    } else {
      setIsLoading(false);
    }
  }, [token, fetchProfile]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        userRole,
        isAuthenticated: !!token,
        isLoading,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
